import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./themeUpdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import {
  pickPuzzleTypeFromThemeData,
  PUZZLE_SHAPE_OPTIONS,
} from "../../../puzzleShapeFromTheme";
import { buildThemeAssetUrl } from "../../../utils/themeAssetUrl";
import mascotPreviewUrl from "../../../img/mascot.jpg";

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_SIZE_MB = 5;
const PUZZLE_DIM = 550;
const GRID_MIN = 2;
const GRID_MAX = 7;

/** Bundled path so preview works under WAMP/subpaths (avoids `/img/...` from site root). Superadmin fallback only. */
const DEFAULT_THEME_UPDATE_PREVIEW = mascotPreviewUrl;

/** Legacy `selected_jigsaw_type` + `jigsaw_type` array for API consumers. */
function persistFieldsForPuzzleShape(shape) {
  const selected_jigsaw_type = shape === "square" ? "square" : "classic";
  const jigsaw_type = ["square", "classic", "irregular"];
  return { selected_jigsaw_type, jigsaw_type };
}

function parseTimer(timerVal) {
  if (!timerVal || typeof timerVal !== "string") {
    return { minutes: 5, seconds: 0 };
  }
  const parts = timerVal.split(":");
  const minutes = Math.max(0, parseInt(parts[0], 10) || 0);
  const seconds = Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0));
  return { minutes, seconds };
}

function parseUseTimeoutFromTheme(data) {
  if (!data) return true;
  const v = data.use_timeout;
  if (v === undefined || v === null || v === "") return true;
  if (v === true) return true;
  if (v === false) return false;
  const s = String(v).toLowerCase().trim();
  if (s === "false" || s === "0" || s === "no" || s === "off") return false;
  if (s === "true" || s === "1" || s === "yes" || s === "on") return true;
  return true;
}

function formatTimerDisplay(minutes, seconds) {
  const m = Math.max(1, minutes);
  let s = seconds;
  if (s < 0) s = 0;
  if (s > 59) s = 59;
  const fm = m < 10 ? `0${m}` : String(m);
  const fs = s < 10 ? `0${s}` : String(s);
  return `${fm}:${fs}`;
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const isSuperadmin = adminToken === "admin&admin";
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [row, setRow] = useState(5);
  const [col, setCol] = useState(5);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [puzzleFile, setPuzzleFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [puzzleShape, setPuzzleShape] = useState("interlock");
  const [useTimer, setUseTimer] = useState(true);
  const [puzzlePreviewFailed, setPuzzlePreviewFailed] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const r = Number(data.row);
    const c = Number(data.col);
    setRow(
      Number.isFinite(r) ? Math.min(GRID_MAX, Math.max(GRID_MIN, r)) : 5
    );
    setCol(
      Number.isFinite(c) ? Math.min(GRID_MAX, Math.max(GRID_MIN, c)) : 5
    );
    const t = parseTimer(data.timer);
    setMinutes(t.minutes < 1 ? 1 : t.minutes);
    setSeconds(t.seconds);
    setPuzzleShape(pickPuzzleTypeFromThemeData(data));
    setUseTimer(parseUseTimeoutFromTheme(data));

    const path = data.puzzle_image ? buildThemeAssetUrl(data.puzzle_image) : null;
    setPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return path || null;
    });
    setPuzzleFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setPuzzlePreviewFailed(false);
  }, [data]);

  const timerDisplay = formatTimerDisplay(minutes, seconds);

  const syncTimerInputs = (m, s) => {
    let mm = m;
    let ss = s;
    if (mm < 1) mm = 1;
    if (ss < 0) ss = 0;
    if (ss > 59) ss = 59;
    setMinutes(mm);
    setSeconds(ss);
  };

  const handleMinutesChange = (e) => {
    const v = parseInt(e.target.value, 10);
    syncTimerInputs(Number.isNaN(v) ? 1 : v, seconds);
  };

  const handleSecondsChange = (e) => {
    const v = parseInt(e.target.value, 10);
    syncTimerInputs(minutes, Number.isNaN(v) ? 0 : v);
  };

  const handlePuzzleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPuzzleFile(null);
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      await Swal.fire("Invalid format", `Use ${VALID_EXT.join(", ")} only.`, "error");
      e.target.value = "";
      return;
    }
    if (!file.type.match(/^image\//)) {
      await Swal.fire("Invalid file", "Only image files are allowed.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      await Swal.fire("File too large", `Max size is ${MAX_SIZE_MB} MB.`, "error");
      e.target.value = "";
      return;
    }
    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== PUZZLE_DIM || height !== PUZZLE_DIM) {
        await Swal.fire(
          "Invalid dimensions",
          `Image must be exactly ${PUZZLE_DIM}×${PUZZLE_DIM} pixels.`,
          "error"
        );
        e.target.value = "";
        return;
      }
    } catch {
      await Swal.fire("Error", "Could not read image dimensions.", "error");
      e.target.value = "";
      return;
    }
    setPuzzleFile(file);
    setPuzzlePreviewFailed(false);
    setPreviewUrl((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleUploadPuzzle = async () => {
    if (!currentTheme) {
      await Swal.fire("No theme", "Select a theme from the admin home first.", "warning");
      return;
    }
    if (!puzzleFile) {
      await Swal.fire("No file", "Choose an image to upload.", "warning");
      return;
    }
    const extraData = {
      themeName: data?.themeName ?? "",
      themeDescription: data?.themeDescription ?? "",
    };
    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append("extraData", JSON.stringify(extraData));
    formData.append("puzzle_image", puzzleFile);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: formData,
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      await Swal.fire({ title: "Successfully updated", icon: "success" });
      dispatch(fetchThemeData({ themeId: currentTheme }));
      setPuzzleFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      await Swal.fire("Error", err.message || "Upload failed", "error");
    }
  };

  const handleSaveGridAndTimer = async () => {
    if (!currentTheme) {
      await Swal.fire("No theme", "Select a theme from the admin home first.", "warning");
      return;
    }
    const r = Math.min(
      GRID_MAX,
      Math.max(GRID_MIN, parseInt(String(row), 10) || 5)
    );
    const c = Math.min(
      GRID_MAX,
      Math.max(GRID_MIN, parseInt(String(col), 10) || 5)
    );
    setRow(r);
    setCol(c);

    let m = parseInt(String(minutes), 10) || 1;
    let s = parseInt(String(seconds), 10) || 0;
    if (m < 1) m = 1;
    if (s < 0) s = 0;
    if (s > 59) s = 59;
    syncTimerInputs(m, s);
    const saveTime = formatTimerDisplay(m, s);

    try {
      const { selected_jigsaw_type, jigsaw_type } =
        persistFieldsForPuzzleShape(puzzleShape);

      await updateThemeData({
        payload: {
          data: {
            row: r,
            col: c,
            timer: saveTime,
            use_timeout: useTimer ? "true" : "false",
            jigsaw_type,
            selected_jigsaw_type,
            selected_puzzle_shape: puzzleShape,
          },
          currentTheme,
        },
        token: adminToken,
      });
      await Swal.fire({ title: "Data updated", icon: "success" });
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (e) {
      await Swal.fire(
        "Error",
        e.message || "Failed to save grid and timer.",
        "error"
      );
    }
  };

  if (!currentTheme) {
    return (
      <div className="theme-update-page">
        <div className="theme-update-no-theme">
          <p>No theme selected.</p>
          <button
            type="button"
            className="back-button"
            onClick={() => navigate("/admin/")}
          >
            <i className="fa-solid fa-arrow-left" /> Back to themes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-update-page">
      <div className="theme-update-inner">
        <div className="back-button-holder theme-update-back">
          <button
            type="button"
            className="back-button"
            onClick={() => navigate("/admin/rules")}
          >
            <i className="fa-solid fa-arrow-left" /> Back
          </button>
        </div>

        <div className="theme-update-columns">
          <div className="theme-update-col">
            <div className="theme-update-card">
              <h2 className="theme-update-card-title">Add Jigsaw Image</h2>
              <div className="theme-update-file-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  className="theme-update-file-input"
                  onChange={handlePuzzleFile}
                />
              </div>
              <p className="theme-update-hint">
                <code>* Allowed formats: JPG, JPEG, PNG</code>
                <code>* Dimensions: {PUZZLE_DIM}×{PUZZLE_DIM}</code>
                <code>* Max file size: {MAX_SIZE_MB} MB</code>
              </p>
              <div className="theme-update-actions">
                <button
                  type="button"
                  className="theme-update-btn-primary"
                  onClick={handleUploadPuzzle}
                  disabled={!puzzleFile}
                >
                  Upload
                </button>
              </div>
              <div className="theme-update-preview">
                {previewUrl ? (
                  <img
                    src={
                      isSuperadmin && puzzlePreviewFailed
                        ? DEFAULT_THEME_UPDATE_PREVIEW
                        : previewUrl
                    }
                    alt="Jigsaw puzzle preview"
                    onError={(e) => {
                      if (!isSuperadmin) return;
                      if (puzzlePreviewFailed) {
                        e.currentTarget.onerror = null;
                        return;
                      }
                      setPuzzlePreviewFailed(true);
                    }}
                  />
                ) : (
                  <span className="theme-update-preview-placeholder">
                    No image yet
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="theme-update-col">
            <div className="theme-update-card">
              <h2 className="theme-update-card-title">Select Grid Size</h2>
              <p className="theme-update-hint">
                Rows and columns set the puzzle grid size (higher = more pieces).
              </p>
              <div className="theme-update-row-container">
                <span>Row</span>
                <input
                  type="number"
                  min={GRID_MIN}
                  max={GRID_MAX}
                  value={row}
                  onChange={(e) => setRow(e.target.value)}
                />
              </div>
              <div className="theme-update-row-container">
                <span>Column</span>
                <input
                  type="number"
                  min={GRID_MIN}
                  max={GRID_MAX}
                  value={col}
                  onChange={(e) => setCol(e.target.value)}
                />
              </div>

              <h3 className="theme-update-section-title">Timer</h3>
              <div className="theme-update-timer-toggle">
                <label htmlFor="tu-use-timer">Countdown timer on game page</label>
                <Switch
                  id="tu-use-timer"
                  checked={useTimer}
                  onChange={setUseTimer}
                  onColor="#4caf50"
                  offColor="#ccc"
                  aria-checked={useTimer}
                />
              </div>
              <p className="theme-update-hint theme-update-hint--inline">
                When off, players have no time limit; the values below are still saved for
                if you turn the timer on later.
              </p>
              <div
                className={`theme-update-timer-grid${useTimer ? "" : " theme-update-timer-grid--disabled"}`}
              >
                <div>
                  <label htmlFor="tu-minutes">Minutes</label>
                  <input
                    id="tu-minutes"
                    type="number"
                    min={1}
                    value={minutes}
                    onChange={handleMinutesChange}
                    disabled={!useTimer}
                  />
                </div>
                <div>
                  <label htmlFor="tu-seconds">Seconds</label>
                  <input
                    id="tu-seconds"
                    type="number"
                    min={0}
                    max={59}
                    value={seconds}
                    onChange={handleSecondsChange}
                    disabled={!useTimer}
                  />
                </div>
                <p className="theme-update-timer-display">{timerDisplay}</p>
              </div>
             

              <div className="theme-update-jigsaw-type">
                <label className="theme-update-jigsaw-type-label" htmlFor="tu-puzzle-shape">
                  Puzzle shape
                </label>
                <select
                  id="tu-puzzle-shape"
                  className="theme-update-select"
                  value={puzzleShape}
                  onChange={(e) => setPuzzleShape(e.target.value)}
                >
                  {PUZZLE_SHAPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="theme-update-actions">
                <button
                  type="button"
                  className="theme-update-btn-primary"
                  onClick={handleSaveGridAndTimer}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeUpdate;
